import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Workshop: Connected Accounts section for Settings.
 * Minimal provider rows with connect/disconnect toggle.
 */

interface Connection {
  id: string;
  provider: string;
  icon: React.ReactNode;
  connected: boolean;
  accountName?: string;
}

const INITIAL_CONNECTIONS: Connection[] = [
  {
    id: "google",
    provider: "Google",
    icon: <Icons.Globe className="h-4 w-4" />,
    connected: true,
    accountName: "alex@gmail.com",
  },
  {
    id: "github",
    provider: "GitHub",
    icon: <Icons.Github className="h-4 w-4" />,
    connected: true,
    accountName: "alexchen",
  },
  {
    id: "slack",
    provider: "Slack",
    icon: <Icons.MessageSquare className="h-4 w-4" />,
    connected: false,
  },
];

interface ConnectedAccountsSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function ConnectedAccountsSection({
  className,
  "data-testid": testId = "settings-connected-accounts",
}: ConnectedAccountsSectionProps) {
  const [connections, setConnections] =
    useState<Connection[]>(INITIAL_CONNECTIONS);

  function handleToggle(id: string) {
    setConnections((prev) =>
      prev.map((conn) => {
        if (conn.id !== id) return conn;
        const nextConnected = !conn.connected;
        return {
          ...conn,
          connected: nextConnected,
          accountName: nextConnected
            ? `mock-${conn.provider.toLowerCase()}`
            : undefined,
        };
      }),
    );
  }

  return (
    <div className={cn("flex flex-col", className)} data-testid={testId}>
      {connections.map((conn, i) => (
        <div
          key={conn.id}
          className={cn(
            "flex items-center gap-3 py-3",
            i < connections.length - 1 && "border-b border-glass-stroke-subtle",
          )}
          data-testid={`connection-${conn.id}`}
        >
          {/* Icon */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-btn-ghost-fill text-secondary">
            {conn.icon}
          </div>

          {/* Provider info */}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-xs font-medium text-primary">
              {conn.provider}
            </span>
            {conn.connected && conn.accountName ? (
              <span className="truncate text-[10px] text-secondary">
                {conn.accountName}
              </span>
            ) : (
              <span className="text-[10px] text-muted">Not connected</span>
            )}
          </div>

          {/* Toggle button */}
          <Button
            variant={conn.connected ? "ghost" : "secondary"}
            size="sm"
            onClick={() => handleToggle(conn.id)}
            data-testid={`connection-toggle-${conn.id}`}
          >
            {conn.connected ? "Disconnect" : "Connect"}
          </Button>
        </div>
      ))}
    </div>
  );
}

export type { ConnectedAccountsSectionProps };
